// Shared constants, filter state, and small components for Thailand views
import { useState, useEffect, useMemo } from 'react';
import { Th } from '../../utils/api';

export const APP_COLORS = {
  // Food delivery
  'Grab Food':     '#00B14F',
  'Lineman Food':  '#F9A825',
  'Robinhood':     '#6366f1',
  'Shopee Food':   '#EE4D2D',
  // Ride hailing
  'Grab Rides':    '#00B14F',
  'Bolt':          '#34D186',
  'InDriver':      '#A7E92F',
  'Lineman Rides': '#F9A825',
  'Maxim':         '#EF4444',
  'ALL':           '#1DB89A',
};
export const FALLBACK_COLORS = ['#1DB89A','#6366f1','#f59e0b','#ef4444','#8b5cf6','#ec4899','#378ADD'];
export function appColor(name, i) { return APP_COLORS[name] || FALLBACK_COLORS[i % FALLBACK_COLORS.length]; }

// Per-alpha accent colours and labels
export const ALPHA_META = {
  open_app:               { color: '#1DB89A', label: 'Open App' },
  transactional_activity: { color: '#6366f1', label: 'Transactional Activity' },
  driver_activity:        { color: '#f59e0b', label: 'Driver Activity' },
};
export const ALPHA_COLORS = Object.fromEntries(Object.entries(ALPHA_META).map(([k, v]) => [k, v.color]));
export const ALPHA_LABELS = Object.fromEntries(Object.entries(ALPHA_META).map(([k, v]) => [k, v.label]));

export function FilterSelect({ label, value, onChange, options }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span className="filter-label">{label}</span>
      <select className="filter-select" value={value} onChange={e => onChange(e.target.value)}>
        <option value="ALL">ALL</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

const EMPTY_FILTERS = {
  app_names: [], alphas: [], provinces: [], districts: [],
  weeks: [], months: [], categories: [], province_district_map: {},
};

/**
 * useThFilters — loads filter options for a vertical and owns the shared
 * filter-bar state (app/alpha/province/district/category cascades).
 * Views are remounted (key={vertical}) when the vertical changes, so each
 * instance loads its filters exactly once.
 */
export function useThFilters(vertical) {
  const [filters,  setFilters]  = useState(EMPTY_FILTERS);
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [appNames, setAppNames] = useState([]);
  const [alphas,   setAlphas]   = useState([]);
  const [province, setProvince] = useState('ALL');
  const [district, setDistrict] = useState('ALL');
  const [category, setCategory] = useState('ALL');
  const [week,     setWeek]     = useState('');

  useEffect(() => {
    setFiltersLoaded(false);
    Th.filters(vertical).then(data => {
      const f = data?.[0] || {};
      setFilters({
        app_names:             f.app_names             || [],
        alphas:                f.alphas                || [],
        provinces:             f.provinces              || [],
        districts:             f.districts              || [],
        weeks:                 f.weeks                 || [],
        months:                f.months                || [],
        categories:            f.categories            || [],
        province_district_map: f.province_district_map || {},
      });
      if (f.categories?.length) setCategory(f.categories[0]);
      if (f.weeks?.length)      setWeek(f.weeks[0]);
      setFiltersLoaded(true);
    }).catch(() => setFiltersLoaded(true));
  }, [vertical]);

  const availableDistricts = useMemo(() => {
    if (province === 'ALL') return filters.districts;
    return filters.province_district_map[province] || [];
  }, [province, filters.districts, filters.province_district_map]);

  function handleProvinceChange(value) {
    setProvince(value);
    setDistrict('ALL');
  }

  function handleDistrictChange(value) {
    setDistrict(value);
    if (value !== 'ALL') {
      const matched = Object.entries(filters.province_district_map)
        .find(([, districts]) => districts.includes(value));
      if (matched) setProvince(matched[0]);
    }
  }

  return {
    filters, filtersLoaded,
    appNames, setAppNames,
    alphas, setAlphas,
    province, handleProvinceChange,
    district, handleDistrictChange,
    category, setCategory,
    week, setWeek,
    availableDistricts,
  };
}
